<script setup>
import { onMounted, onBeforeUnmount, watch, ref } from 'vue'

const props = defineProps({
  positions: { type: Array, default: () => [] },
  vehicles:  { type: Array, default: () => [] },
  route:     { type: Array, default: () => [] },
  center:    { type: Array, default: () => [13.7563, 100.5018] },
  zoom:      { type: Number, default: 13 },
})

const mapEl  = ref(null)
let   map    = null
let   markers = {}
let   routeLine = null
let   resizeObserver = null

const statusColor = {
  available:   'var(--ok)',
  rented:      'var(--accent)',
  maintenance: 'var(--warn)',
  retired:     'var(--muted)',
}

function vehicleForDevice(deviceId) {
  return props.vehicles.find(v => v.traccar_device_id === String(deviceId))
}

function drawMarkers(positions) {
  if (!map) return
  const L = window.L
  const seen = new Set()

  positions.forEach(pos => {
    const id  = pos.deviceId
    const lat = pos.latitude
    const lng = pos.longitude
    if (!lat || !lng) return
    seen.add(id)

    const vehicle = vehicleForDevice(id)
    const color   = statusColor[vehicle?.status] || 'var(--muted)'
    const label   = vehicle ? `${vehicle.make} ${vehicle.model}` : `Device ${id}`
    const speed   = pos.speed != null ? ` · ${Math.round(pos.speed)} km/h` : ''
    const popup   = vehicle
      ? `<b>${label}</b><br>${vehicle.registration}${speed}<br><a href="/vehicles/${vehicle.id}/gps">View GPS →</a>`
      : `<b>${label}</b>${speed}`

    const icon = L.divIcon({
      className: '',
      html: `<div style="width:14px;height:14px;border-radius:50%;background:${color};border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,.4)"></div>`,
      iconSize: [14, 14],
      iconAnchor: [7, 7],
    })

    if (markers[id]) {
      markers[id].setLatLng([lat, lng]).setPopupContent(popup)
    } else {
      markers[id] = L.marker([lat, lng], { icon }).bindPopup(popup).addTo(map)
    }
  })

  Object.keys(markers).forEach(id => {
    if (!seen.has(Number(id))) {
      map.removeLayer(markers[id])
      delete markers[id]
    }
  })
}

function drawRoute(route) {
  if (!map || !route.length) return
  const L = window.L
  if (routeLine) map.removeLayer(routeLine)
  const latlngs = route.map(p => [p.latitude, p.longitude])
  const rootStyle = getComputedStyle(document.documentElement)
  const accent    = rootStyle.getPropertyValue('--accent').trim() || '#2563eb'
  routeLine = L.polyline(latlngs, { color: accent, weight: 3, opacity: 0.7 }).addTo(map)
  map.fitBounds(routeLine.getBounds(), { padding: [30, 30] })
}

onMounted(async () => {
  const L = (await import('leaflet')).default
  await import('leaflet/dist/leaflet.css')
  window.L = L

  map = L.map(mapEl.value).setView(props.center, props.zoom)
  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    maxZoom: 19,
  }).addTo(map)

  if (props.route.length) {
    drawRoute(props.route)
  } else {
    drawMarkers(props.positions)
  }

  const ro = new ResizeObserver(() => {
    if (map) map.invalidateSize()
  })
  ro.observe(mapEl.value)
  resizeObserver = ro
})

onBeforeUnmount(() => {
  if (resizeObserver) { resizeObserver.disconnect(); resizeObserver = null }
  if (map) { map.remove(); map = null }
})

watch(() => props.positions, (newPositions) => {
  drawMarkers(newPositions)
}, { deep: true })

watch(() => props.route, (newRoute) => {
  drawRoute(newRoute)
}, { deep: true })
</script>

<template>
  <div ref="mapEl" class="livemap w-full h-[400px] overflow-hidden" :style="{ border: '1px solid var(--line)', borderRadius: 'var(--radius-table)' }"></div>
</template>
